import { useState, type ReactNode } from 'react'
import { mdiChevronDown, mdiChevronRight } from '@mdi/js'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible'
import { AppIcon } from './ui/icon'

interface ContractTypeGroupSectionProps {
  /** The group's contract type code, `none` for untyped contracts. */
  typeCode: string
  title: string
  /** The count label (« 3 contrats »). */
  countLabel: string
  toggleLabel: string
  /** Decorations after the count: a status pill, a coverage line, an add action. */
  headerSlot?: ReactNode
  defaultOpen?: boolean
  /** A group nobody expects here any more (not applicable): its title reads muted. */
  muted?: boolean
  children?: ReactNode
}

/**
 * One contract type (« poste ») of an asset's contracts: a header with the
 * type name and its count, open by default, folding its rows away.
 */
export function ContractTypeGroupSection({
  typeCode, title, countLabel, toggleLabel, headerSlot, defaultOpen = true, muted = false, children,
}: ContractTypeGroupSectionProps) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <Collapsible open={open} onOpenChange={setOpen} asChild>
      <section className="border-b border-border last:border-b-0" data-testid={`contracts-group-${typeCode}`}>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 bg-muted/40 px-4 py-2 md:flex-nowrap">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className={`flex min-w-0 items-center gap-2 text-left text-sm font-semibold hover:text-primary ${muted ? 'text-muted-foreground' : 'text-foreground'}`}
              aria-label={toggleLabel}
              data-testid={`contracts-group-toggle-${typeCode}`}
            >
              <AppIcon path={open ? mdiChevronDown : mdiChevronRight} size="sm" tone="muted" className="shrink-0" />
              <span className="truncate">{title}</span>
            </button>
          </CollapsibleTrigger>
          <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground" data-testid={`contracts-group-count-${typeCode}`}>
            {countLabel}
          </span>
          {headerSlot && <div className="ml-auto flex min-w-0 items-center justify-end gap-2">{headerSlot}</div>}
        </div>
        <CollapsibleContent>{children}</CollapsibleContent>
      </section>
    </Collapsible>
  )
}
